import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ColumnsErrorBanner, {
  getColumnErrors,
  GetRequiredColumnsErrorFunction,
  RequiredColumnsErrorState,
} from 'bulk-import/panes/ColumnsErrorBanner';
import MonomerMappingForm from 'bulk-import/panes/MonomerMappingForm';
import SpreadsheetColumnsMappingDropdown, {
  SpreadsheetColumnsMapping,
} from 'bulk-import/saved-column-mappings/SpreadsheetColumnsMappingDropdown';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  COLUMN_TYPE_TO_NAME,
  FileData,
  GetValidColumnTypesFunction,
  GetSchemaFieldsToHideByColumnFunction,
  IMPORT_TYPE,
  MAX_TIME_LIMIT_MINUTES,
  OPTIONS,
  RegistryImportSettingsProps,
  SelectedSchemaIds,
  SpreadsheetColumn,
  TaskState,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportOption from 'bulk-import/SpreadsheetBulkImportOption';
import {SelectedDestination} from 'bulk-import/SpreadsheetBulkImportStore';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {getSchemaFromColumnType} from 'bulk-import/SpreadsheetBulkImportUtil';
import SpreadsheetColumnsTable from 'bulk-import/SpreadsheetColumnsTable';
import {getValidContentsContainerColumns} from 'bulk-import/storables/StorableColumnsUtil';
import {MinimalFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import MultipleFolderSelector from 'file-browser/MultipleFolderSelector';
import {
  SpreadsheetColumnsMappingsColumnType,
  TagSchemaSequenceSchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {getComplexPolymerSchemaImportSubmenuOptions} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import isBatchesEnabled from 'registry/IsBatchesEnabled';
import * as RegistryUtil from 'registry/RegistryUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {AnonymousSchema} from 'schemas/SchemaConstants';
import useTagSchemaQuery from 'schemas/useTagSchemaQuery';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';
import {listToReadable} from 'util/NameUtil';
import {NumericInputChangeEvent} from 'util/Types';

import {TagSchemaGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaGraphQL';

function getHelpTextForDeleteEmptyFields(importType: IMPORT_TYPE): string {
  const clearedContents = ['schema field values'];
  if (importType === IMPORT_TYPE.UPDATE_BIOENTITIES) {
    clearedContents.push('bioentity contents');
  }
  if (SpreadsheetBulkImportUtil.isForUpdateFieldsWithCustomFields(importType)) {
    clearedContents.push('custom fields');
  }
  if (importType === IMPORT_TYPE.UPDATE_CONTAINERS) {
    const includedColumns = getValidContentsContainerColumns();
    for (const column of includedColumns) {
      clearedContents.push(pluralize(2, COLUMN_TYPE_TO_NAME[column]!.toLowerCase()));
    }
  }
  return `If this option is selected, ${listToReadable(
    clearedContents
  )} will be cleared from empty cells. Otherwise, empty cells are ignored.`;
}

export interface QueryingMapColumnNamesModalPaneProps {
  fileData: FileData;
  importType: IMPORT_TYPE;
  linkFolderIds: Array<string>;
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  options: {
    [key in OPTIONS]: boolean;
  };
  registryImportSettings: RegistryImportSettingsProps;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
  selectedSchemaIds: SelectedSchemaIds;
  tableColumnNames?: Array<string>;
  dataTableKeyFields?: Array<COLUMN_TYPE>;
  _timeLimit?: number | null;
  getValidColumnTypes: GetValidColumnTypesFunction;
  getSchemaFieldsToHideByColumnType?: GetSchemaFieldsToHideByColumnFunction;
  getRequiredColumnsError: GetRequiredColumnsErrorFunction;
  shouldResolveLinkColumns: boolean;
  linkItemsFolderSelectorLabel: string;
  linkItemsFolderSelectorDescription?: string;
  onValidateSpreadsheet: () => void;
  getValidSchemaFieldIds?: (columnIndex: number) => Array<string>;
  customHandleGoToNextPaneFn?: (props: MapColumnNamesModalPaneProps) => void;
  savedMappingsConfig?: {
    enableSavedMappings: boolean;
    uploadFlow: string;
  };
  complexPolymerSchemaConfiguration?: ComplexPolymerSchemaConfigGraphQL | null;
  selectedDestination: SelectedDestination | null;
}

export interface MapColumnNamesModalPaneProps extends QueryingMapColumnNamesModalPaneProps {
  tagSchema: TagSchemaGraphQL | null;
}

export interface MapColumnNamesModalPaneState {
  allFolders: Array<MinimalFolder> | null;
  // spreadsheetColumnsMappingWarnings contains warnings about the last selected mapping, and is cleared only
  // when a new mapping is selected, or when the columns are reset
  spreadsheetColumnsMappingWarnings: Set<string>;
  defaultMappingOverridden: boolean;
  defaultMapping: Array<SpreadsheetColumn> | null;
}

interface MapColumnNamesModalPaneConfigOptions {
  getValidColumnTypes: GetValidColumnTypesFunction;
  getSchemaFieldsToHideByColumnType?: GetSchemaFieldsToHideByColumnFunction;
  getRequiredColumnsError: GetRequiredColumnsErrorFunction;
  onValidateSpreadsheet: () => void;
  getValidSchemaFieldIds?: (columnIndex: number) => Array<string>;
  shouldResolveLinkColumns?: boolean;
  linkItemsFolderSelectorLabel?: string;
  linkItemsFolderSelectorDescription?: string;
  customHandleGoToNextPaneFn?: (props: MapColumnNamesModalPaneProps) => void;
  savedMappingsConfig?: {
    enableSavedMappings: boolean;
    uploadFlow: string;
  };
}

export interface MapColumnNamesModalPaneConfig extends MapColumnNamesModalPaneConfigOptions {
  shouldResolveLinkColumns: boolean;
  linkItemsFolderSelectorLabel: string;
  linkItemsFolderSelectorDescription: string;
  customHandleGoToNextPaneFn?: (props: MapColumnNamesModalPaneProps) => void;
}

interface ColumnTypeDetails {
  type: SpreadsheetColumnsMappingsColumnType;
  schemaFieldId: string | null;
}

/**
 * Returns configuration for MapColumnNamesModalPane for a given import flow.
 * @param {getValidColumnTypes}           Called with (extraState) and should return an array of
 *                                        column types.
 * @param {getSchemaFieldsToHideByColumnType}   Should return a map of column types to schema field
 *                                        `displayLabels` that should be hidden.
 * @param {getRequiredColumnsError}       Called with (columns, extraState) and should return a
 *                                        string error if the columns are missing required columns,
 *                                        null otherwise.
 * @param {onValidateSpreadsheet}         Action to validate spreadsheet
 * @param {shouldResolveLinkColumns}      If specified, determines whether when link fields are selected
 *                                        we need to show folder selector. If not specified, defaults to
 *                                        true.
 * @param {customHandleGoToNextPaneFn}    Custom handler to go to the next pane, required if the flow
 *                                        needs to handle skipping or branching panes
 */
export function configureMapColumnNamesModalPane({
  getValidColumnTypes,
  getSchemaFieldsToHideByColumnType,
  getRequiredColumnsError,
  onValidateSpreadsheet,
  getValidSchemaFieldIds,
  shouldResolveLinkColumns = true,
  linkItemsFolderSelectorLabel = 'Look for linked items in:',

  linkItemsFolderSelectorDescription = 'Choose any folders that we should search in to find your item ' +
    'links. Entities that will be created from the spreadsheet are considered automatically.',

  customHandleGoToNextPaneFn,
  savedMappingsConfig,
}: MapColumnNamesModalPaneConfigOptions): MapColumnNamesModalPaneConfig {
  assert(
    getValidColumnTypes != null && getRequiredColumnsError != null && onValidateSpreadsheet != null,
    'MapColumnNamesModalPane is incorrectly configured.'
  );
  return {
    getValidColumnTypes,
    getSchemaFieldsToHideByColumnType,
    getRequiredColumnsError,
    onValidateSpreadsheet,
    getValidSchemaFieldIds,
    shouldResolveLinkColumns,
    linkItemsFolderSelectorLabel,
    linkItemsFolderSelectorDescription,
    customHandleGoToNextPaneFn,
    savedMappingsConfig,
  };
}

export class MapColumnNamesModalPane extends React.Component<
  MapColumnNamesModalPaneProps,
  MapColumnNamesModalPaneState
> {
  state: MapColumnNamesModalPaneState = {
    allFolders: null,
    spreadsheetColumnsMappingWarnings: new Set(),
    defaultMappingOverridden: false,
    defaultMapping: null,
  };

  /** Private method, exposed for testing */
  _handleSelectorLoadAllFolders = (folders: Array<MinimalFolder>): void => {
    this.setState({allFolders: folders});
    const registryFolders = folders.filter((folder) => RegistryUtil.isRegistry(folder.source));

    if (this.props.registryImportSettings.registry != null) {
      // Autoselect registry folder if we have registry import settings
      assertAndContinue(
        registryFolders.find(
          (registryFolder) =>
            registryFolder.id === this.props.registryImportSettings.registry?.rootFolder?.api_identifier
        ) != null,
        'Registry folder not found in all folders',
        {registryFolders, registryImportSettings: this.props.registryImportSettings}
      );
      SpreadsheetBulkImportActions.setImportSetting({
        linkFolderIds: [this.props.registryImportSettings.registry?.rootFolder.api_identifier],
      });
    } else if (this.props.selectedDestination?.folderId != null) {
      // Otherwise, autoselect selected destination if there is one
      assertAndContinue(
        folders.find((folder) => folder.id === this.props.selectedDestination?.folderId) != null,
        'Selected destination folder not found in all folders',
        {folders, selectedDestination: this.props.selectedDestination}
      );
      SpreadsheetBulkImportActions.setImportSetting({
        linkFolderIds: [this.props.selectedDestination.folderId],
      });
    } else if (registryFolders.length === 1) {
      // Otherwise, autoselect registry if there is only one
      SpreadsheetBulkImportActions.setImportSetting({linkFolderIds: [registryFolders[0].api_identifier]});
    }
  };

  /** Private method, exposed for testing */
  _handleSelectLinkFolders = (folders: Array<MinimalFolder>): void => {
    const linkFolderIds = folders.map((folder) => folder.api_identifier);
    SpreadsheetBulkImportActions.setImportSetting({linkFolderIds});
  };

  private handleSetTimeLimit = (e: NumericInputChangeEvent): void => {
    SpreadsheetBulkImportActions.setTimeLimit(e.target.numericValue! * 60);
  };

  private handleGoToNextPane = (): void => {
    if (this.props.customHandleGoToNextPaneFn) {
      this.props.customHandleGoToNextPaneFn(this.props);
    } else {
      this.props.onValidateSpreadsheet();
      SpreadsheetBulkImportActions.goToNextPane();
    }
  };

  private getSchemaFieldFromId = (schema: AnonymousSchema | null, id: string): SchemaField | undefined => {
    const allSchemaFields = schema?.allFields;
    return allSchemaFields?.find((field) => field.apiId === id);
  };

  /** We cannot set the defaultMapping on init, as the default column values may not have yet been passed in
   * from the parent component. This controls the behavior of the "Reset to default" button. */
  private setDefaultMappingIfUnset = (): void => {
    if (this.state.defaultMapping == null) {
      this.setState({defaultMapping: this.props.fileData.columns});
    }
  };

  private handleSetColumns = (columns: Array<SpreadsheetColumn>): void => {
    this.setDefaultMappingIfUnset();
    SpreadsheetBulkImportActions.setColumns(columns);
    SpreadsheetBulkImportActions.setSavedColumnMappingName(null);
    this.setState({defaultMappingOverridden: true});
  };

  private handleSelectSpreadsheetMappings = (mapping: SpreadsheetColumnsMapping): void => {
    this.setDefaultMappingIfUnset();
    this.setState({defaultMappingOverridden: true});

    const individualMappings = mapping.individualColumnMappings;
    const columnNameToColumnType: {[name: string]: ColumnTypeDetails} = {};
    for (const individualMapping of individualMappings) {
      columnNameToColumnType[individualMapping.columnName] = {
        type: individualMapping.columnType,
        schemaFieldId: individualMapping.schemaField?.apiId ? individualMapping.schemaField?.apiId : null,
      };
    }

    const allValidColumnTypes = this.getValidColumnTypes();
    const columnNames = this.props.fileData.columns.map((data) => data.name);
    const newColumns: Array<SpreadsheetColumn> = [];
    const warnings = new Set<string>();

    const namesOfColumnsMissingFromSpreadsheetColumnsMapping = new Set<string>();
    const namesOfColumnsFromSpreadsheetColumnsMappingMissingFromSpreadsheet = new Set<string>();
    const namesOfColumnsNotAvailable = new Set<string>();

    for (const [columnIndex, name] of columnNames.entries()) {
      if (Object.keys(columnNameToColumnType).includes(name)) {
        const columnType = COLUMN_TYPE[columnNameToColumnType[name].type];
        if (SpreadsheetBulkImportUtil.getColumnTypeToSendToServer(columnType) === COLUMN_TYPE.SCHEMA_FIELD) {
          const schema = getSchemaFromColumnType(
            COLUMN_TYPE[columnNameToColumnType[name].type],
            this.props.selectedSchemaIds
          );
          const field = this.getSchemaFieldFromId(
            schema,
            individualMappings.find((mapping) => mapping.columnName === name)!.schemaField!.apiId
          );
          const validSchemaFieldIds = this.props.getValidSchemaFieldIds?.(columnIndex);
          // Only filter out schema fields if we have a list of valid schema field ids
          if (!field || (validSchemaFieldIds && !validSchemaFieldIds.includes(field.apiId))) {
            namesOfColumnsNotAvailable.add(name);
            newColumns.push({name, type: COLUMN_TYPE.IGNORE});
          } else {
            newColumns.push({
              name,
              type: columnType,
              schemaField: field,
            });
          }
        } else if (!allValidColumnTypes.includes(columnType)) {
          namesOfColumnsNotAvailable.add(name);
          newColumns.push({name, type: COLUMN_TYPE.IGNORE});
        } else {
          newColumns.push({name, type: columnType});
        }
      } else {
        newColumns.push({name, type: COLUMN_TYPE.IGNORE});
        namesOfColumnsMissingFromSpreadsheetColumnsMapping.add(name);
      }
    }

    Object.keys(columnNameToColumnType).forEach((name) => {
      if (!columnNames.includes(name)) {
        namesOfColumnsFromSpreadsheetColumnsMappingMissingFromSpreadsheet.add(name);
      }
    });

    if (namesOfColumnsMissingFromSpreadsheetColumnsMapping.size > 0) {
      const count = namesOfColumnsMissingFromSpreadsheetColumnsMapping.size;
      warnings.add(
        `The ${pluralize(count, 'column')} "${Array.from(
          namesOfColumnsMissingFromSpreadsheetColumnsMapping
        ).join('", "')}" ${pluralize(
          count,
          'was',
          'were'
        )} not found in the saved column mapping. Please select the intended column type.`
      );
    }

    if (namesOfColumnsFromSpreadsheetColumnsMappingMissingFromSpreadsheet.size > 0) {
      const count = namesOfColumnsFromSpreadsheetColumnsMappingMissingFromSpreadsheet.size;
      warnings.add(
        `The ${pluralize(count, 'column')} "${Array.from(
          namesOfColumnsFromSpreadsheetColumnsMappingMissingFromSpreadsheet
        ).join('", "')}" ${pluralize(
          count,
          'was',
          'were'
        )} found in the saved column mapping but missing from the spreadsheet. Check to make sure all intended columns are listed.`
      );
    }

    if (namesOfColumnsNotAvailable.size > 0) {
      const count = namesOfColumnsNotAvailable.size;
      warnings.add(
        `The column ${pluralize(count, 'type')} for "${Array.from(namesOfColumnsNotAvailable).join(
          '", "'
        )}" ${pluralize(count, 'is', 'are')} not available. Please select the intended column type.`
      );
    }

    SpreadsheetBulkImportActions.setColumns(newColumns);
    SpreadsheetBulkImportActions.setSavedColumnMappingName(mapping.name);
    this.setState({spreadsheetColumnsMappingWarnings: warnings});
  };

  private handleResetToDefaultSpreadsheetMappings = (): void => {
    if (this.state.defaultMapping == null) {
      return;
    }

    SpreadsheetBulkImportActions.setColumns(this.state.defaultMapping);
    SpreadsheetBulkImportActions.setSavedColumnMappingName(null);
    this.setState({
      spreadsheetColumnsMappingWarnings: new Set(),
      defaultMappingOverridden: false,
    });
  };

  private handleConfirmIgnore = (): void => {
    const updatedColumns = SpreadsheetBulkImportUtil.confirmIgnoredColumns(this.props.fileData.columns);
    SpreadsheetBulkImportActions.setColumns(updatedColumns);
  };

  private getValidColumnTypes = (): Array<COLUMN_TYPE> =>
    this.props
      .getValidColumnTypes({
        registryImportSettings: this.props.registryImportSettings,
        selectedSchemaIds: this.props.selectedSchemaIds,
        options: this.props.options,
        dataTableKeyFields: this.props.dataTableKeyFields,
        schemalessCreationEntityType: this.props.schemalessCreationEntityType,
      })
      .filter(
        (columnType) =>
          isBatchesEnabled() || ![COLUMN_TYPE.BATCH_NAME, COLUMN_TYPE.INPUT_BATCH_NAME].includes(columnType)
      );

  private getSchemaFieldsToHideByColumnType = (): Map<COLUMN_TYPE, Array<string>> => {
    if (!this.props.getSchemaFieldsToHideByColumnType) {
      return new Map();
    }
    return this.props.getSchemaFieldsToHideByColumnType();
  };

  private hasLinkColumns = (): boolean =>
    this.props.fileData.columns &&
    this.props.fileData.columns.some(
      (column) =>
        (SpreadsheetBulkImportUtil.getColumnTypeToSendToServer(column.type) === COLUMN_TYPE.SCHEMA_FIELD &&
          TagSchemaUtil.isItemLinkFieldType(column.schemaField!.fieldType)) ||
        column.type === COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT
    );

  private isWaitingForLinkFoldersToLoad = (): boolean => {
    if (!this.props.shouldResolveLinkColumns || !this.hasLinkColumns()) {
      return false;
    }
    // If link columns are specified, prevent clicking Next if the multiple folder selector is still
    // loading link folders (Once they are loaded, allFolders becomes non-null)
    return this.state.allFolders == null;
  };

  private shouldDisplayFolderSelector = (): boolean =>
    this.props.importType === IMPORT_TYPE.UPDATE_MIXTURE_INGREDIENTS ||
    (this.props.shouldResolveLinkColumns && this.hasLinkColumns());

  private shouldDisplaySavedMappingsDropdown = (): boolean =>
    Boolean(this.props.savedMappingsConfig) &&
    Boolean(
      this.props.selectedSchemaIds.resultSchemaId != null || this.props.selectedSchemaIds.tagSchemaId != null
    );

  private shouldDisplaySavedMappingsWarnings = (): boolean =>
    this.state.spreadsheetColumnsMappingWarnings.size > 0;

  private shouldDisplayUpdateExistingColumnsBanner = (): boolean =>
    SpreadsheetBulkImportUtil.isForUpdateFields(this.props.importType) &&
    this.props.fileData.columns &&
    this.props.fileData.columns.some((column) =>
      [COLUMN_TYPE.CUSTOM_FIELD, COLUMN_TYPE.SCHEMA_FIELD].includes(
        SpreadsheetBulkImportUtil.getColumnTypeToSendToServer(column.type)
      )
    );

  private sampleControlPropagationWarning = (): JSX.Element | null => {
    const warningText = SpreadsheetBulkImportUtil.getSampleControlRolesPropagationWarning(
      this.props.fileData.columns,
      this.props.importType
    );
    return warningText ? (
      <div className="u-small-padding-bottom">
        <InlineBanner key="sample-control" bannerType={InlineBannerType.INFO} supportingInfo={warningText} />
      </div>
    ) : null;
  };

  private shouldDisplayMonomerMapping = (): boolean => {
    const {tagSchema} = this.props;
    const isEligibleSchema = Boolean(
      tagSchema &&
        (tagSchema.sequenceType === TagSchemaSequenceSchemaType.RNA_SEQUENCE ||
          tagSchema.sequenceType === TagSchemaSequenceSchemaType.RNA_OLIGO)
    );
    const isEligibleSchemalessCreationEntityType =
      this.props.schemalessCreationEntityType === ModelTypeConstants.RNA_SEQUENCE ||
      this.props.schemalessCreationEntityType === ModelTypeConstants.RNA_OLIGO;
    const isRelevantImportType = [
      IMPORT_TYPE.INVENTORY_FILES,
      IMPORT_TYPE.REGISTRY_ENTITIES,
      IMPORT_TYPE.UPDATE_BIOENTITIES,
    ].includes(this.props.importType);
    const hasBasesColumn = this.props.fileData.columns?.find((col) => col.type === COLUMN_TYPE.RESIDUES);

    return Boolean(
      BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES &&
        (isEligibleSchema || isEligibleSchemalessCreationEntityType) &&
        isRelevantImportType &&
        hasBasesColumn
    );
  };

  render(): JSX.Element {
    // Extra state from SpreadsheetBulkImportStore used for calculating getRequiredColumnsError.
    const requiredColumnsErrorState: RequiredColumnsErrorState = {
      options: this.props.options,
      registryImportSettings: this.props.registryImportSettings,
      selectedSchemaIds: this.props.selectedSchemaIds,
      dataTableKeyFields: this.props.dataTableKeyFields,
      importType: this.props.importType,
      schemalessCreationEntityType: this.props.schemalessCreationEntityType,
    };
    return (
      <Modal
        enableRedesign={false}
        buttons={[
          {
            key: 'back',
            text: 'Back',
            onClick: SpreadsheetBulkImportActions.goToPrevPane,
          },
          {
            key: 'next',
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: this.handleGoToNextPane,
            disabled:
              this.props.fileData.state !== TaskState.COMPLETED ||
              this.isWaitingForLinkFoldersToLoad() ||
              getColumnErrors(
                this.props.fileData,
                this.props.getRequiredColumnsError,
                requiredColumnsErrorState
              ).length > 0,
            dataTestAttrs: {
              component: 'MapColumnNamesModal',
              element: 'footerButton',
              key: ModalButtonKey.NEXT,
            },
          },
        ]}
        scrollable={false}
        expandHeight={false}
        {...this.props.modalProps}
      >
        <p>Here&apos;s how we interpreted your spreadsheet header. Does this look correct?</p>
        {this.shouldDisplaySavedMappingsDropdown() && (
          <div className="u-small-spacing-bottom">
            <SpreadsheetColumnsMappingDropdown
              assaySchemaId={this.props.selectedSchemaIds.resultSchemaId ?? null}
              tagSchemaId={this.props.selectedSchemaIds.tagSchemaId ?? null}
              // Checks that this.props.savedMappingsConfig is not null in this.shouldDisplaySavedMappingsDropdown
              uploadFlow={this.props.savedMappingsConfig!.uploadFlow}
              handleSelect={this.handleSelectSpreadsheetMappings}
            />
          </div>
        )}
        <SpreadsheetColumnsTable
          columnTypes={this.getValidColumnTypes()}
          columns={this.props.fileData.columns}
          importType={this.props.importType}
          setColumns={this.handleSetColumns}
          selectedSchemaIds={this.props.selectedSchemaIds}
          tableColumnNames={this.props.tableColumnNames}
          dataTableKeyFields={this.props.dataTableKeyFields}
          getValidSchemaFieldIds={this.props.getValidSchemaFieldIds}
          schemaFieldsToHideByColumnType={this.getSchemaFieldsToHideByColumnType()}
          schemalessCreationEntityType={this.props.schemalessCreationEntityType}
          {...(this.props.complexPolymerSchemaConfiguration && {
            additionalSubmenuOptionsByColumnType: {
              [COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT]: getComplexPolymerSchemaImportSubmenuOptions(
                this.props.complexPolymerSchemaConfiguration
              ),
            },
          })}
        />
        {this.shouldDisplaySavedMappingsDropdown() && (
          <Button
            className={css(styles.resetToDefaultMappingButton)}
            onClick={this.handleResetToDefaultSpreadsheetMappings}
            buttonStyle={ButtonStyle.TEXT}
            disabled={!this.state.defaultMappingOverridden}
            size={ButtonSize.MEDIUM}
            text="Reset to default"
          />
        )}
        <ColumnsErrorBanner
          fileData={this.props.fileData}
          getRequiredColumnsError={this.props.getRequiredColumnsError}
          importType={this.props.importType}
          requiredColumnsErrorState={requiredColumnsErrorState}
          onConfirmIgnore={this.handleConfirmIgnore}
        />
        {this.shouldDisplaySavedMappingsWarnings() &&
          Array.from(this.state.spreadsheetColumnsMappingWarnings).map((warningText, index) => (
            <div key={index} className="u-small-padding-bottom">
              <InlineBanner bannerType={InlineBannerType.WARNING} supportingInfo={warningText} />
            </div>
          ))}
        {this.sampleControlPropagationWarning()}
        {this.shouldDisplayUpdateExistingColumnsBanner() && (
          <div className="u-small-padding-bottom">
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              supportingInfo="The values in your uploaded spreadsheet will overwrite any existing values in the selected fields."
            />
          </div>
        )}

        {this.shouldDisplayFolderSelector() && (
          <div className="form-group">
            <label>{this.props.linkItemsFolderSelectorLabel}</label>
            <div className="faded u-small-padding-bottom">
              {this.props.linkItemsFolderSelectorDescription}
            </div>
            <MultipleFolderSelector
              onLoadAllFolders={this._handleSelectorLoadAllFolders}
              onSelectFolders={this._handleSelectLinkFolders}
              selectedFolderIds={this.props.linkFolderIds}
              showInventory={true}
            />
          </div>
        )}
        {this.props.importType === IMPORT_TYPE.UPDATE_BIOENTITIES && (
          <SpreadsheetBulkImportOption
            helpContent={
              <div>
                If this option is selected, all existing aliases for matching entities will be deleted.
              </div>
            }
            isChecked={this.props.options[OPTIONS.DELETE_EXISTING_ALIASES]}
            name={OPTIONS.DELETE_EXISTING_ALIASES}
            text="Delete existing aliases from matching entities"
          />
        )}
        {SpreadsheetBulkImportUtil.isForUpdateFields(this.props.importType) && (
          <SpreadsheetBulkImportOption
            helpContent={<div>{getHelpTextForDeleteEmptyFields(this.props.importType)}</div>}
            isChecked={this.props.options[OPTIONS.DELETE_EMPTY_FIELDS]}
            name={OPTIONS.DELETE_EMPTY_FIELDS}
            text="Delete or clear values for empty cells"
          />
        )}
        {this.props.importType === IMPORT_TYPE.UPDATE_MIXTURE_INGREDIENTS && (
          <SpreadsheetBulkImportOption
            helpContent={
              <div>
                If this option is selected, empty cells for Catalog Identifier, Source, and Amount columns
                will clear those same values on existing ingredients. Otherwise, empty cells are ignored.
              </div>
            }
            isChecked={this.props.options[OPTIONS.CLEAR_INGREDIENT_FIELDS_FOR_EMPTY_CELLS]}
            name={OPTIONS.CLEAR_INGREDIENT_FIELDS_FOR_EMPTY_CELLS}
            text="Clear ingredient fields for empty cells"
          />
        )}
        {hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) &&
          SpreadsheetBulkImportUtil.doesImportTypeSupportClearingSampleRoles(this.props.importType) && (
            <SpreadsheetBulkImportOption
              helpContent={
                <div>
                  {`If this option is selected, empty cells will result in
                  ${getRestrictedSampleUsersDisplayText()} and sample owners
                  being cleared${
                    this.props.importType === IMPORT_TYPE.TRANSFER_STORABLES
                      ? ' from destination containers'
                      : ''
                  }. Otherwise, empty cells are ignored.`}
                </div>
              }
              isChecked={this.props.options[OPTIONS.CLEAR_SAMPLE_ROLES_FOR_EMPTY_CELLS]}
              name={OPTIONS.CLEAR_SAMPLE_ROLES_FOR_EMPTY_CELLS}
              text={`Clear ${getRestrictedSampleUsersDisplayText()} and sample owners for empty cells`}
            />
          )}
        {this.shouldDisplayMonomerMapping() && (
          <MonomerMappingForm
            onSetMonomerMapping={SpreadsheetBulkImportActions.updateMonomerMapping}
            sectionHeader={
              <div className="u-small-spacing-bottom">
                Additionally, please specify how Benchling should interpret the nucleotide bases in your
                spreadsheet.
              </div>
            }
          />
        )}
        {SpreadsheetBulkImportUtil.canOverrideTimeout() && (
          <div className="u-small-spacing-top">
            <span>Optionally set a time limit, up to 30 minutes, for the import flow.</span>
            <NumericInput
              max={MAX_TIME_LIMIT_MINUTES}
              onChange={this.handleSetTimeLimit}
              placeholder="Time limit (minutes)"
              value={this.props._timeLimit ? this.props._timeLimit / 60 : this.props._timeLimit}
            />
          </div>
        )}
      </Modal>
    );
  }
}

export default function QueryingMapColumnNamesModalPane(
  props: QueryingMapColumnNamesModalPaneProps
): JSX.Element {
  const tagSchemaId = props.selectedSchemaIds.tagSchemaId;
  const {tagSchema} = useTagSchemaQuery(tagSchemaId);
  return <MapColumnNamesModalPane {...props} tagSchema={tagSchema} />;
}

const styles = StyleSheet.create({
  resetToDefaultMappingButton: {
    paddingLeft: '0px',
  },
});
